import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Chip,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Web as WebIcon,
  AutoFixHigh as SurgicalIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import useNotification from '../../hooks/useNotification';

const WireframePageConversation = ({ 
  projectId, 
  pageId, 
  pageTitle,
  onClose,
  onPageUpdate 
}) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [page, setPage] = useState(null);
  const [loadingPage, setLoadingPage] = useState(true);
  
  const messageListRef = useRef(null);
  const { showNotification } = useNotification();

  // Load page data and initialize conversation
  useEffect(() => {
    loadPageData();
    initializeConversation();
  }, [pageId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  const loadPageData = async () => {
    try {
      setLoadingPage(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/wireframes/projects/${projectId}/pages/${pageId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load page: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setPage(data.data.page);
      }
    } catch (error) {
      console.error('Error loading page:', error);
      showNotification({
        message: `Failed to load page: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoadingPage(false);
    }
  };

  const initializeConversation = () => {
    setMessages([
      {
        role: 'assistant',
        content: `I'm ready to help you update the **${pageTitle}** page with surgical precision! 

I can make specific changes like:
- "Change the header background to blue"
- "Add a search box to the navigation"
- "Make the buttons larger"
- "Add a sidebar with menu items"
- "Update the footer text"

What would you like to modify on this page?`,
        timestamp: new Date().toISOString(),
        isIntro: true
      }
    ]);
  };

  const handleSurgicalUpdate = async (e) => {
    e?.preventDefault();
    if (!message.trim() || isUpdating) return;

    const userMessage = {
      role: 'user',
      content: message.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage('');
    setIsUpdating(true);

    try {
      // Add loading message
      const loadingMessage = {
        role: 'assistant',
        content: 'Making surgical updates to the page...',
        timestamp: new Date().toISOString(),
        isLoading: true
      };
      setMessages(prev => [...prev, loadingMessage]);

      // Call surgical update API
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/wireframes/projects/${projectId}/pages/${pageId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            user_message: userMessage.content,
            context: { 
              conversation_mode: true,
              preserve_styling: true,
              update_mode: 'surgical_precision'
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Surgical update failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Remove loading message and add success response
        setMessages(prev => prev.filter(m => !m.isLoading));
        
        const updatedPage = data.data.page;
        setPage(updatedPage);

        // Notify parent of update
        if (onPageUpdate) {
          onPageUpdate(pageId, updatedPage);
        }

        // Add success message with stats
        const tokenSavings = data.data.token_savings || 0;
        const toolsUsed = data.data.tools_used || 0;
        const executionTime = data.data.execution_time || 0;

        const successMessage = {
          role: 'assistant',
          content: `✅ **Page updated successfully with surgical precision!**

${data.data.changes_summary || 'Your changes have been applied to the page.'}

**Update Stats:**
${tokenSavings > 1000 ? `- 🎯 Token Savings: ${Math.round(tokenSavings/1000)}k tokens saved` : ''}
${toolsUsed > 0 ? `- 🔧 Tools Used: ${toolsUsed} surgical tools` : ''}
${executionTime > 0 ? `- ⚡ Execution Time: ${executionTime.toFixed(2)}s` : ''}

The page preview has been updated. What else would you like to modify?`,
          timestamp: new Date().toISOString(),
          isSuccess: true
        };

        setMessages(prev => [...prev, successMessage]);

        showNotification({
          message: `Page updated surgically! ${tokenSavings > 1000 ? `Saved ${Math.round(tokenSavings/1000)}k tokens` : ''}`,
          type: 'success'
        });

      } else {
        throw new Error(data.error || 'Update failed');
      }

    } catch (error) {
      console.error('Error in surgical update:', error);
      
      // Remove loading message
      setMessages(prev => prev.filter(m => !m.isLoading));
      
      // Add error message
      const errorMessage = {
        role: 'assistant',
        content: `❌ **Update failed**: ${error.message}

Please try rephrasing your request or being more specific about what you want to change.`,
        timestamp: new Date().toISOString(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);

      showNotification({
        message: `Surgical update failed: ${error.message}`,
        type: 'error'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const renderPagePreview = () => {
    if (loadingPage) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
          <Typography sx={{ ml: 2 }}>Loading page...</Typography>
        </Box>
      );
    }

    if (!page?.html_content) {
      return (
        <Box sx={{ 
          p: 4, 
          textAlign: 'center',
          bgcolor: '#f5f5f5',
          borderRadius: 1,
          border: '2px dashed #ddd'
        }}>
          <WebIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            No content generated yet
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        border: '1px solid #ddd', 
        borderRadius: 1, 
        overflow: 'hidden',
        height: '400px'
      }}>
        <iframe 
          srcDoc={page.html_content}
          title={`${pageTitle} Preview`}
          style={{ 
            width: '100%', 
            height: '100%', 
            border: 'none',
            background: 'white'
          }}
          sandbox="allow-scripts allow-same-origin"
        />
      </Box>
    );
  };

  const renderMessages = () => {
    return messages.map((msg, index) => (
      <Box key={index} sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.300',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5
          }}>
            <Typography variant="body2" color={msg.role === 'user' ? 'white' : 'text.primary'} fontWeight={600}>
              {msg.role === 'user' ? 'Y' : 'A'}
            </Typography>
          </Box>
          <Typography variant="subtitle2" fontWeight={600}>
            {msg.role === 'user' ? (msg.createdByName || msg.created_by_name || 'You') : 'Surgical AI Assistant'}
          </Typography>
          {msg.isSuccess && (
            <Chip 
              icon={<SurgicalIcon />}
              label="Surgical Update" 
              size="small" 
              color="success" 
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        
        <Box sx={{ ml: 6 }}>
          <Paper sx={{
            p: 2,
            bgcolor: msg.role === 'user' ? 'rgba(25, 118, 210, 0.08)' : 'white',
            border: msg.isError ? '1px solid #f44336' : 
                   msg.isSuccess ? '1px solid #4caf50' : 
                   msg.isLoading ? '1px solid #ff9800' : 
                   '1px solid #e0e0e0'
          }}>
            {msg.isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography>{msg.content}</Typography>
              </Box>
            ) : (
              <ReactMarkdown>{msg.content}</ReactMarkdown>
            )}
          </Paper>
        </Box>
      </Box>
    ));
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <SurgicalIcon sx={{ mr: 1, color: 'primary.main' }} />
            Surgical Editor: {pageTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Make precise changes to individual page elements
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', flexGrow: 1, height: 'calc(100% - 70px)' }}>
        {/* Left: Conversation */}
        <Box sx={{ 
          width: '50%', 
          borderRight: '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Messages */}
          <Box 
            ref={messageListRef}
            sx={{ 
              flexGrow: 1, 
              overflowY: 'auto', 
              p: 2,
              maxHeight: 'calc(100vh - 300px)'
            }}
          >
            {renderMessages()}
          </Box>

          {/* Input */}
          <Box sx={{ 
            p: 2, 
            borderTop: '1px solid rgba(0,0,0,0.12)',
            bgcolor: 'background.paper'
          }}>
            <form onSubmit={handleSurgicalUpdate}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Describe the changes you want to make..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={isUpdating}
                  multiline
                  maxRows={3}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!message.trim() || isUpdating}
                  sx={{ minWidth: 48, p: 1 }}
                >
                  {isUpdating ? <CircularProgress size={20} /> : <SendIcon />}
                </Button>
              </Box>
            </form>
            
            {/* Quick action buttons */}
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {[
                'Change header color to blue',
                'Add search bar',
                'Make responsive',
                'Add footer'
              ].map((suggestion) => (
                <Button
                  key={suggestion}
                  size="small"
                  variant="outlined"
                  disabled={isUpdating}
                  onClick={() => setMessage(suggestion)}
                  sx={{ fontSize: '0.75rem', py: 0.5 }}
                >
                  {suggestion}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Right: Live Preview */}
        <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid rgba(0,0,0,0.12)',
            bgcolor: 'background.default'
          }}>
            <Typography variant="subtitle1" fontWeight={600}>Live Preview</Typography>
            <Typography variant="body2" color="text.secondary">
              Updates in real-time as you make changes
            </Typography>
          </Box>
          
          <Box sx={{ p: 2, flexGrow: 1, bgcolor: '#f8f9fa' }}>
            {renderPagePreview()}
          </Box>

          {/* Page stats */}
          {page?.structure && (
            <Box sx={{ 
              p: 2, 
              borderTop: '1px solid rgba(0,0,0,0.12)',
              bgcolor: 'background.paper'
            }}>
              <Typography variant="caption" color="text.secondary">
                Elements: {page.structure.elements} • 
                Forms: {page.structure.forms} • 
                Buttons: {page.structure.buttons} • 
                Links: {page.structure.links}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WireframePageConversation;